import { Breadcrumbs, Link, Typography } from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import { useHistory } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";

import DestinationsList from "./components/DestinationsList";
import useAccountMetadata from "./hooks/useAccountMetadata";
import { useAllSaasDestinations } from "./hooks/useAllSaasDestinations";
import Navigation from "./Navigation";
import { SET_SINGLE_DESTINATION_CONFIG } from "./reducers/destinations";
import useArrayOfDestinations from "./hooks/useArrayOfDestinations";
import useFeatureFlags from "./hooks/useFeatureFlag";

const useStyles = makeStyles(() => ({
  content: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  inner: {
    minWidth: 700,
  },
  switchContainerHidden: {
    visibility: "hidden",
  },
  card: {
    width: "65%",
    minWidth: 800,
  },
  navigation: {
    paddingTop: 24,
  },
  clickableImg: {
    "&:hover": {
      cursor: "pointer",
    },
  },
}));

function DestinationsListPage({ filter, userIsFPEmployee }) {
  const allSaasDestinations = useAllSaasDestinations();
  const accountMetadata = useAccountMetadata();
  const flag = useFeatureFlags();

  let isFbPixelEnabledAccount = false;
  if (accountMetadata.loaded && accountMetadata.projects.length > 0) {
    isFbPixelEnabledAccount =
      accountMetadata.projects[0].accountID ===
      "81b6ae81-8007-41e6-82d1-7258cd3bbd20";
  }

  let filteredDestinations = _.chain(allSaasDestinations)
    .values()
    .filter(
      (dest) =>
        // Show Facebook Pixel Proxy in list only if Experimental-dests enabled *and* (FP employee or for a specific customer account)
        (!dest.experimental || flag("experimentalDestinations")) &&
        (dest.key !== "Facebook Pixel Proxy" ||
          userIsFPEmployee ||
          (dest.key === "Facebook Pixel Proxy" && isFbPixelEnabledAccount))
    )
    .sortBy((dest) => dest.name)
    .value();
  if (filter) {
    filteredDestinations = _.filter(filteredDestinations, (dest) =>
      filter.includes(dest.type)
    );
  }
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  // TODO: we need to pass id downstream. This is currently used to lookup dest by type in a bunch of places
  const configuredDestinations = useArrayOfDestinations();

  const setConfiguredDestinations = (destConfig) => {
    dispatch({ type: SET_SINGLE_DESTINATION_CONFIG, payload: destConfig });
  };

  function handleConfigureDest(dest, config) {
    const configSegment = config ? "/" + config.id : "";
    history.push(dest.configPageUrl() + configSegment);
  }

  let breadcrumbType = "Apps";
  if (filter?.includes("cohorting")) {
    breadcrumbType = "Cohorting";
  } else if (filter?.includes("function")) {
    breadcrumbType = "Functions";
  }

  let emptyState = (
    <>
      No destinations have been set up yet. To get started, configure any of the
      destinations below!
    </>
  );
  if (filter?.includes("function") && filteredDestinations.length == 0) {
    emptyState = (
      <>
        No functions have been set up yet. To get started,&nbsp;
        <a href="/functions" className="text-brand-red underline">
          register a destination function
        </a>
        .
      </>
    );
  }

  return (
    <Navigation className={clsx(classes.navigation)}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        className={clsx(classes.breadcrumb)}
      >
        {filter ? (
          <Link color="inherit" onClick={() => history.push(`/destinations`)}>
            <Typography className={clsx(classes.lightText)} variant="overline">
              Destinations
            </Typography>
          </Link>
        ) : (
          <Typography className={clsx(classes.darkText)} variant="overline">
            Destinations
          </Typography>
        )}
        {filter && (
          <Typography className={clsx(classes.darkText)} variant="overline">
            {breadcrumbType}
          </Typography>
        )}
      </Breadcrumbs>
      <Typography component="h1" gutterBottom variant="h3">
        Connect all your analytics and marketing tools
      </Typography>

      <DestinationsList
        destinations={filteredDestinations}
        configuredDestinations={configuredDestinations}
        onConfigure={(dest, config) => handleConfigureDest(dest, config)}
        onConfiguredDestsChange={(state) => setConfiguredDestinations(state)}
        emptyState={emptyState}
      />
    </Navigation>
  );
}

DestinationsListPage.propTypes = {
  filter: PropTypes.arrayOf(
    PropTypes.oneOf([null, "app", "advertising", "cohorting", "function"])
  ),
  userIsFPEmployee: PropTypes.bool,
};

export default DestinationsListPage;
